import React, {useEffect, useMemo, useState} from 'react';
import {
    Box, 
    Button, 
    Drawer, 
    Grid, 
    IconButton, 
    Typography,
    FormControlLabel,
    Checkbox,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingCard from "../common/LoadingCard";

const SubAccountSettingsDrawer = ({getSubAccountApi, updateSubAccountApi}) => {
    const [item, setItem] = useState(null);
    const [availableSubAccounts, setAvailableSubAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Form state
    const [assignedSubAccounts, setAssignedSubAccounts] = useState([]);
    const [initialAssignments, setInitialAssignments] = useState([]);
    
    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const onClose = () => {
        setOpen(false);
        setItem(null);
        setError(null);
        setSuccess(null);
        setAssignedSubAccounts([]);
        setInitialAssignments([]);
    }

    const fetchItemData = (itemId) => {
        setLoading(true);
        setError(null);
        let itemUrl = getSubAccountApi.replace('item:', itemId);
        
        fetch(itemUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then((data) => {
                setItem(data.item);
                setAvailableSubAccounts(data.available_sub_accounts);
                setAssignedSubAccounts((data.assigned_sub_accounts || []).map((entry) => ({
                    id: entry.id,
                    name: entry.name,
                    is_editable: !!entry.is_editable,
                })));
                setInitialAssignments((data.assigned_sub_accounts || []).map((entry) => ({
                    id: entry.id,
                    name: entry.name,
                    is_editable: !!entry.is_editable,
                })));
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching item data:', error);
                setLoading(false);
                setError('Failed to load item data');
            });
    };

    const assignedIdSet = useMemo(() => new Set(assignedSubAccounts.map((entry) => entry.id)), [assignedSubAccounts]);

    const handleToggleSubAccount = (subAccount) => {
        setAssignedSubAccounts((prev) => {
            const exists = prev.find((entry) => entry.id === subAccount.id);
            if (exists) {
                return prev.filter((entry) => entry.id !== subAccount.id);
            }

            return [...prev, {
                id: subAccount.id,
                name: subAccount.name,
                is_editable: false,
            }];
        });
    };

    const handleToggleEditable = (subAccountId) => {
        setAssignedSubAccounts((prev) => prev.map((entry) => {
            if (entry.id === subAccountId) {
                return {
                    ...entry,
                    is_editable: !entry.is_editable,
                };
            }
            return entry;
        }));
    };

    const buildChangeSummary = () => {
        const initialMap = new Map(initialAssignments.map((entry) => [entry.id, !!entry.is_editable]));
        const currentMap = new Map(assignedSubAccounts.map((entry) => [entry.id, !!entry.is_editable]));

        const added = assignedSubAccounts
            .filter((entry) => !initialMap.has(entry.id))
            .map((entry) => `assign ${entry.name}`);

        const removed = initialAssignments
            .filter((entry) => !currentMap.has(entry.id))
            .map((entry) => `remove ${entry.name}`);

        const toggled = assignedSubAccounts
            .filter((entry) => initialMap.has(entry.id) && initialMap.get(entry.id) !== entry.is_editable)
            .map((entry) => `set ${entry.name} editable ${entry.is_editable ? 'ON' : 'OFF'}`);

        return [...added, ...removed, ...toggled];
    };

    const handleSave = () => {
        const changes = buildChangeSummary();

        if (changes.length === 0) {
            setError('No changes to save');
            return;
        }
        
        const message = `Are you sure you want to ${changes.join(' and ')}?`;
        
        setConfirmDialog({
            open: true,
            title: 'Confirm Changes',
            message: message,
            onConfirm: performSave
        });
    };

    const performSave = () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        
        const updateUrl = updateSubAccountApi.replace('item:', item.id);
        const requestData = {
            assigned_sub_accounts: assignedSubAccounts.map((entry) => ({
                id: entry.id,
                is_editable: entry.is_editable,
            }))
        };
        
        fetch(updateUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify(requestData)
        })
        .then((response) => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then((data) => {
            setSuccess('Sub-account settings updated successfully');
            setItem(data.item);
            setAvailableSubAccounts(data.available_sub_accounts || []);
            setAssignedSubAccounts((data.assigned_sub_accounts || []).map((entry) => ({
                id: entry.id,
                name: entry.name,
                is_editable: !!entry.is_editable,
            })));
            setInitialAssignments((data.assigned_sub_accounts || []).map((entry) => ({
                id: entry.id,
                name: entry.name,
                is_editable: !!entry.is_editable,
            })));
            setSaving(false);
            
            // Reload the table after successful update
            if (window.table && window.table.ajax) {
                window.table.ajax.reload();
            }
            
            // Close the drawer after a short delay
            setTimeout(() => {
                onClose();
            }, 1500);
        })
        .catch((error) => {
            console.error('Error updating sub-account settings:', error);
            setError('Failed to update sub-account settings');
            setSaving(false);
        });
        
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
    };

    // Listen for the custom event to open the drawer
    useEffect(() => {
        const handleSubAccountSettings = (event) => {
            const {itemId} = event.detail;
            setOpen(true);
            fetchItemData(itemId);
        }

        window.addEventListener('items:subAccountSettings', handleSubAccountSettings);

        return () => {
            window.removeEventListener('items:subAccountSettings', handleSubAccountSettings);
        }
    }, []);

    const hasChanges = () => {
        if (!item) return false;

        const normalize = (list) => list
            .map((entry) => ({ id: entry.id, is_editable: !!entry.is_editable }))
            .sort((a, b) => a.id - b.id);

        const current = JSON.stringify(normalize(assignedSubAccounts));
        const initial = JSON.stringify(normalize(initialAssignments));

        return current !== initial;
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: 400 }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Sub Account Settings</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loading && <LoadingCard />}
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {item && !loading && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Item: {item.name}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Assign Sub Accounts
                                </Typography>
                                {availableSubAccounts.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        No sub-accounts available for assignment.
                                    </Typography>
                                )}
                                {availableSubAccounts.map((subAccount) => {
                                    const isSelected = assignedIdSet.has(subAccount.id);
                                    const assignedEntry = assignedSubAccounts.find((entry) => entry.id === subAccount.id);

                                    return (
                                        <Box
                                            key={subAccount.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 1,
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleToggleSubAccount(subAccount)}
                                                        disabled={saving}
                                                    />
                                                }
                                                label={subAccount.name}
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption">Editable</Typography>
                                                <Switch
                                                    checked={!!assignedEntry?.is_editable}
                                                    onChange={() => handleToggleEditable(subAccount.id)}
                                                    disabled={!isSelected || saving}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={!hasChanges() || saving}
                                    startIcon={saving ? <CircularProgress size={20} /> : null}
                                    fullWidth
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Drawer>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
            >
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDialog.onConfirm} 
                        color="primary" 
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SubAccountSettingsDrawer;